import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import type OrderItem from '@this/domain/order_item2';
import { Link } from '@this/shared/ui/navigations/link';
import { Flex } from '@this/shared/ui/layout/flex';
import Tooltip from '@this/shared/tooltip/tooltip';

type Props = {
  orderItem: OrderItem;
};

type HotelReservation = {
  reserveUrl: string;
  singleReserveUrl: string;
  alternativePlanUrl?: string;
};

const toSingleReserveUrl = (original: string) => {
  if (original) {
    return original.replace(/f_otona_su=\d/i, 'f_otona_su=1');
  }
  return original;
};

const toAlternativePlanUrl = (original: string) => {
  if (!original) return undefined;

  try {
    const url = new URL(original);

    if (!url.searchParams.get('f_hi1')) return undefined;

    const searchUrl = new URL('https://travel.rakuten.co.jp/hotel/search/');
    searchUrl.searchParams.set('f_teikei', '&');
    searchUrl.searchParams.set('f_dai', url.searchParams.get('f_dai') || '');
    searchUrl.searchParams.set('f_chu', url.searchParams.get('f_chu') || '');
    searchUrl.searchParams.set('f_shou', url.searchParams.get('f_shou') || '');
    searchUrl.searchParams.set('f_nen1', url.searchParams.get('f_nen1') || '');
    searchUrl.searchParams.set('f_tuki1', url.searchParams.get('f_tuki1') || '');
    searchUrl.searchParams.set('f_hi1', url.searchParams.get('f_hi1') || '');
    searchUrl.searchParams.set('f_nen2', url.searchParams.get('f_nen2') || '');
    searchUrl.searchParams.set('f_tuki2', url.searchParams.get('f_tuki2') || '');
    searchUrl.searchParams.set('f_hi2', url.searchParams.get('f_hi2') || '');
    searchUrl.searchParams.set('f_heya_su', url.searchParams.get('f_heya_su') || '');
    searchUrl.searchParams.set('f_otona_su', url.searchParams.get('f_otona_su') || '');
    searchUrl.searchParams.set('f_s1_kodate_su', url.searchParams.get('f_s1_kodate_su') || '');
    searchUrl.searchParams.set('f_s1_syougaku_su', url.searchParams.get('f_s1_syougaku_su') || '');
    searchUrl.searchParams.set('f_s1_youji_su', url.searchParams.get('f_s1_youji_su') || '');
    searchUrl.searchParams.set('f_s1_nyuji_su', url.searchParams.get('f_s1_nyuji_su') || '');

    return searchUrl.toString();
  } catch {
    return undefined;
  }
};

export const OrderItemReservationUrls = observer(({ orderItem }: Props) => {
  const packageBookingUrl = useMemo(() => orderItem.packageBookingUrl, [orderItem]);

  const hotelReservations = useMemo<HotelReservation[]>(() => {
    const urls: HotelReservation[] = [];
    orderItem.hotels.forEach(hotel => {
      if (hotel.reserveUrl) {
        urls.push({
          reserveUrl: hotel.reserveUrl,
          singleReserveUrl: toSingleReserveUrl(hotel.reserveUrl),
          alternativePlanUrl: toAlternativePlanUrl(hotel.reserveUrl)
        });
      }
    });
    return urls;
  }, [orderItem]);

  if (!packageBookingUrl && hotelReservations.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 style={{ marginBottom: '4px' }}>ホテル/パッケージ予約</h3>

      {packageBookingUrl ? (
        <>
          <Flex>
            <Link href={packageBookingUrl} target="_blank" isExternal>
              パッケージを予約
            </Link>
            <Tooltip place="right">
              <p>リンク先の在庫がない場合は、代替パッケージの検索リンクから商品を探してください</p>
            </Tooltip>
          </Flex>
          {hotelReservations.length > 0 && (
            <Link href={hotelReservations[0].reserveUrl} target="_blank" isExternal>
              代替パッケージの検索
            </Link>
          )}
        </>
      ) : (
        hotelReservations && (
          <Flex flexDirection="column" gap="4px">
            {hotelReservations.map((r, i) => (
              <div key={i.toString()}>
                <p>{i + 1}つ目のホテル</p>
                <ul style={{ marginLeft: '8px' }}>
                  <li style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Link href={r.reserveUrl} target="_blank" isExternal>
                      ホテルを予約(全体)
                    </Link>
                    <Link href={r.singleReserveUrl} target="_blank" isExternal>
                      ホテルを予約(1名ずつ)
                    </Link>
                    <Link href={r.alternativePlanUrl} target="_blank" isExternal>
                      代替プランを探す
                    </Link>
                  </li>
                </ul>
              </div>
            ))}
          </Flex>
        )
      )}
    </div>
  );
});
